import React, { useContext } from 'react'

import { isFunction } from 'lodash-es'

import { type ButtonProps } from '@/components/atoms/Button/Button.tsx'
import {
  type Dropdown,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/Select/Select.tsx'
import { type TeamCarData } from '@/components/molecules/Status'
import { cn } from '@/lib/utils.ts'

import { SelectTeamStatusContext, type SelectTeamStatusValue } from './SelectTeamStatusProvider.tsx'

export interface SelectTeamStatusOptions {
  teamStatusOptions: {
    placeholder: string | ((teamNumber: string) => string)
    options: Array<{ text: string; value: SelectTeamStatusValue }>
  }
  teamAvailableInRegionOptions: Dropdown
}

export interface SelectTeamStatusData {
  team: TeamCarData & { usingMobileApp: boolean }
}

export interface SelectTeamStatusProps
  extends React.HtmlHTMLAttributes<HTMLDivElement>, SelectTeamStatusData, SelectTeamStatusOptions {
  variant?: ButtonProps['variant']
}

const isStatusOptionEnabled = (type: SelectTeamStatusValue, usingMobileApp: boolean) => {
  switch (type) {
    case 'region':
      return !usingMobileApp
    case 'gps':
      return usingMobileApp
    default:
      return true
  }
}

export const SelectTeamStatus = React.forwardRef<HTMLDivElement, SelectTeamStatusProps>(
  ({ className, variant, teamStatusOptions, teamAvailableInRegionOptions, team, ...props }, ref) => {
    const { status, setStatus, region, setRegion } = useContext(SelectTeamStatusContext)

    return (
      <div className={cn('grid gap-4', className)} ref={ref} {...props}>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-fit">
            <SelectValue
              placeholder={
                isFunction(teamStatusOptions.placeholder)
                  ? teamStatusOptions.placeholder(team.carNumber)
                  : teamStatusOptions.placeholder
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {teamStatusOptions.options
                .filter((option) => isStatusOptionEnabled(option.value, team.usingMobileApp))
                .map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.text}
                  </SelectItem>
                ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        {status === 'region' && (
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-fit">
              <SelectValue placeholder={teamAvailableInRegionOptions.placeholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {teamAvailableInRegionOptions.options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.text}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        )}
      </div>
    )
  }
)
